<!-- eslint-disable no-unused-vars -->
<script setup>
/* eslint semi: 1 */
import { computed, inject, getCurrentInstance } from 'vue';
import { FwbTooltip, FwbButton } from 'flowbite-vue';

import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin';
import { useScheduleList } from '@/composables/schedule/use-schedule-list';
import { useScheduleQuery } from '@/composables/schedule/use-schedule-query';
import SchedulePlaytimePopup from './schedule-playtime-popup.vue';

defineOptions({
  mixins: [MediaScheduleMixin]
});

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  showLoader: {
    type: Boolean,
    default: false,
  },
  enableDelete: {
    type: Boolean,
    default: false,
  },
  playerStatusById: {
    type: Object,
    default: () => ({}),
  },
});

defineEmits([
  'card-click',
  'delete-click',
  'preview-load',
  'iframe-mouseenter',
  'iframe-mouseleave',
  'setup-click'
]);

const $utils = inject('$utils');
const { proxy } = getCurrentInstance();
const { getStatus, getPreviewAspectRatio, getPlayerUiState } = useScheduleList();
const { isFiltering } = useScheduleQuery();

const loaderItems = computed(() => [1, 2, 3, 4]);

function getCardKey (item, index) {
  return item?._edit?.uid || item?.scheduleId || item?.id || index;
}

function getPreviewType (item) {
  return item?.preview?.type || null;
}

function isScheduleOnline (item) {
  const rawStatus = props.playerStatusById[item?.scheduleId];
  return rawStatus == null || !['unconfigured', 'failed', 'disconnected'].includes(rawStatus);
}

</script>

<template>
  <div class="schedule-grid-list-wrapper">
    <div class="schedule-grid-list-container flex flex-wrap">
      <!-- Loader -->
      <template v-if="showLoader">
        <div
          v-for="item in loaderItems"
          :key="item"
          class="w-[calc(25%)] p-2"
          data-component-element-id="schedule-grid-item-loader"
        >
          <div class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm px-6">
            <div class="flex justify-between pt-3 pb-4">
              <div class="h-3 w-1/3 rounded bg-gray-200" />
              <div class="h-3 w-1/3 rounded bg-gray-200" />
            </div>
            <div class="animate-pulse bg-gray-200 aspect-[97/55] rounded-lg" />
            <div class="space-y-2 p-3">
              <div class="h-3 w-2/3 rounded bg-gray-200" />
              <div class="h-2 w-1/6 rounded bg-gray-100" />
              <div class="h-2 w-full rounded bg-gray-100" />
            </div>
          </div>
        </div>
      </template>

      <!-- Items -->
      <template v-else-if="items?.length > 0">
        <div
          v-for="(schedule, index) in items"
          :key="getCardKey(schedule, index)"
          :id="`scheduleGridCard-${getCardKey(schedule, index)}`"
          class="schedule-grid-item group relative w-[calc(25%)] p-2"
          :data-component-element-id="`schedule-grid-item-${getCardKey(schedule, index)}`"
        >
          <div
            class="relative overflow-hidden rounded-lg border bg-white shadow-sm transition-all duration-150 border-gray-200 hover:border-gray-300 hover:shadow-md hover:bg-gray-100"
          >
            <div class="absolute right-3 top-3 z-[2] flex items-center gap-2">
              <button
                v-if="enableDelete"
                type="button"
                class="inline-flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent transition opacity-0 group-hover:opacity-100"
                :aria-label="$t('label.delete')"
                :data-component-element-id="`schedule-grid-item-delete-btn-${getCardKey(schedule, index)}`"
                @click.stop="$emit('delete-click', schedule)"
              >
                <div class="w-full h-full schedule-delete-icon" />
              </button>
            </div>

            <div class="relative">
              <div class="flex justify-between px-6 pt-3 pb-4">
                <div
                  class="text-sm text-gray-500 font-medium"
                  :data-component-element-id="`schedule-grid-item-model-${getCardKey(schedule, index)}`"
                >
                  {{ schedule.deviceType }}
                </div>
                <div
                  class="flex rounded-lg px-[10px] py-[2px] capitalize text-xs font-medium"
                  :class="getStatus(isScheduleOnline(schedule))?.class ?? 'text-gray-800 bg-gray-100'"
                  :data-component-element-id="`schedule-grid-item-status-${getCardKey(schedule, index)}`"
                >
                  <BaseIconSvg
                    :name="getStatus(isScheduleOnline(schedule))?.icon ?? ''"
                    class="w-4! h-4!"
                  />
                  {{ $t(getStatus(isScheduleOnline(schedule))?.text ?? '') }}
                </div>
              </div>

              <div class="relative px-6 hover:bg-gray-100">
                <div
                  class="overflow-hidden rounded-md w-full aspect-[97/55]"
                  :data-component-element-id="`schedule-grid-item-preview-${getCardKey(schedule, index)}`"
                >
                  <img
                    v-if="getPreviewType(schedule) === 'img'"
                    :src="schedule.preview.url"
                    :alt="schedule.name"
                    :style="getPreviewAspectRatio(schedule).isVertical ? { aspectRatio: getPreviewAspectRatio(schedule).ratio } : {}"
                    :class="['h-full object-cover', getPreviewAspectRatio(schedule).isVertical ? 'block w-auto mx-auto' : 'w-full']"
                    @load="$emit('preview-load', schedule)"
                  />

                  <iframe
                    v-else-if="['svg', 'html'].includes(getPreviewType(schedule))"
                    :id="`scheduleGridPreview-${schedule.scheduleId || getCardKey(schedule, index)}`"
                    :src="$utils.filterMediaPreviewUrl(schedule.preview.url, { queryParams: { dimensions: schedule.dimensions } })"
                    :data-src="$utils.trimDataSrc(schedule.preview.url, { queryParams: { dimensions: schedule.dimensions } })"
                    :style="getPreviewAspectRatio(schedule).isVertical ? { aspectRatio: getPreviewAspectRatio(schedule).ratio } : {}"
                    :class="{
                      'template-target-on-refresh-token border-0': true,
                      'object-fit_cover_center': !getPreviewAspectRatio(schedule).isVertical,
                      'block h-full w-auto mx-auto object-cover object-center': getPreviewAspectRatio(schedule).isVertical,
                      [`media-preview-${schedule?._edit?.uid}`]: true
                    }"
                    @load="$emit('preview-load', schedule)"
                    @mouseenter="$emit('iframe-mouseenter', schedule)"
                    @mouseleave="$emit('iframe-mouseleave', schedule)"
                  />

                  <div
                    v-else
                    class="flex h-full w-full items-center justify-center bg-gray-100 text-center"
                  >
                    <span class="px-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {{$t('label.previewUnavailable')}}
                    </span>
                  </div>
                </div>
              </div>
              <div
                class="absolute inset-0 z-[1] cursor-pointer transition-opacity duration-150"
                :class="{
                  'bg-[#D9D9D9B2]' : getPlayerUiState(schedule) === 'unconfigured',
                  'bg-gray-100 opacity-0 group-hover:opacity-50' : getPlayerUiState(schedule) !== 'unconfigured'
                }"
                @click="$emit('card-click', schedule)"
              >
                <FwbButton
                  v-if="getPlayerUiState(schedule) === 'unconfigured'"
                  class="flex items-center border-0 outline-none mt-[35%] mx-auto w-fit px-3 py-2 rounded-lg text-white"
                  color="red"
                  @click="$emit('setup-click', schedule)"
                >
                  <template #prefix>
                    <img class="w-3 h-3" src="@/static/icons/player_icon_white.svg" />
                  </template>
                  {{ $t('label.setup') }}
                </FwbButton>
              </div>
            </div>

            <div class="space-y-3 py-3 px-6">
              <div class="flex items-start justify-between gap-2">
                <p
                  class="line-clamp-1 text-xs font-bold uppercase tracking-wide text-gray-800"
                  :data-component-element-id="`schedule-grid-item-name-${getCardKey(schedule, index)}`"
                >
                  {{ schedule.name }}
                </p>
              </div>

              <!-- Media duration -->
              <div class="flex flex-wrap items-center gap-1.5">
                <FwbTooltip
                  v-if="schedule?.mediaDurations?.content != null"
                  placement="top"
                >
                  <template #trigger>
                    <span
                      class="inline-flex items-center rounded border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-700"
                      :data-component-element-id="`schedule-grid-item-duration-${getCardKey(schedule, index)}`"
                    >
                      {{$utils.formatDurationToMinutesAndSeconds({ duration: schedule.mediaDurations.content })}}
                    </span>
                  </template>
                  <template #content>
                    {{ $t('label.duration') }}
                  </template>
                </FwbTooltip>

                <!-- Adds duration -->
                <FwbTooltip
                  v-if="schedule?.mediaDurations?.adds"
                  placement="top"
                >
                  <template #trigger>
                    <span
                      class="inline-flex items-center rounded bg-[#9FA0AD] px-2 py-0.5 text-[10px] font-semibold text-[#121212]"
                      :data-component-element-id="`schedule-grid-item-ads-duration-${getCardKey(schedule, index)}`"
                    >
                      {{$utils.formatDurationToMinutesAndSeconds({ duration: schedule.mediaDurations.adds })}}
                    </span>
                  </template>
                  <template #content>
                    {{ $t('label.adsDuration') }}
                  </template>
                </FwbTooltip>
              </div>

              <!-- Play time -->
              <div class="flex flex-wrap gap-1 items-center">
                <template v-if="schedule?.times?.length > 0">
                  <div
                    v-for="(time, timeIndex) in proxy.filterScheduleTimes(schedule?.times).slice(0, 1)"
                    :key="`${getCardKey(schedule, index)}-time-${timeIndex}`"
                    class="rounded-lg px-2 py-0.5 flex shrink-0 justify-center items-center"
                    :data-component-element-id="`schedule-grid-item-time-${getCardKey(schedule, index)}-${timeIndex}`"
                    :class="{
                      'bg-primary-100 text-primary-800': !time?.priority,
                      'bg-orange-100 text-orange-800': time?.priority
                    }"
                  >
                    <BaseIconSvg
                      v-if="time?.priority"
                      name="flowbite:star-solid"
                      class="mr-1 inline-block w-4! h-4!"
                    />
                    <BaseIconSvg
                      v-else
                      name="flowbite:calendar-month-outline"
                      class="mr-1 inline-block w-4! h-4!"
                    />
                    <span
                      class="text-sm font-medium flex items-center whitespace-nowrap"
                      :class="{
                        'text-primary-800': !time?.priority,
                        'text-orange-800': time?.priority
                      }"
                    >
                      {{time?._edit?.textLabel}}
                    </span>
                  </div>
                  <SchedulePlaytimePopup
                    v-if="schedule?.times?.length > 1"
                    :playTimes="proxy.filterScheduleTimes(schedule?.times)"
                  />
                </template>
                <div v-else class="h-6">
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="flex w-full flex-col items-center justify-center gap-2 py-20">
          <p class="text-xs font-bold uppercase tracking-wide text-gray-700">
            {{ isFiltering
            ? $t('texts.emptyFilterTablePlaceholder')
            : $t('texts.noContentHasBeenScheduledYet')}}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.object-fit_cover_center {
  box-sizing: border-box;
  width: 100% !important;
  height: 100% !important;
  object-position: center center;
  object-fit: cover;
}

.schedule-delete-icon {
  background: url('../../../static/icons/remove.svg') no-repeat center center;
  background-size: contain;
}
</style>
